import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import If from "components/common/If";
import { useTranslation } from "react-i18next";
import { DatalistDataSourceFilterDefinitionType, DatalistTableColumnType } from "types/datalist-types";
import { DropdownInputPropsType } from "types/form-types";
import { Conditions, filterConditions } from "utilities/datalist/datalist-filter-utilities";
import { getTranslationText } from "utilities/translation-utilities";
import DatalistFilterInput from "./value-inputs/DatalistFilterInput";

type DatalistFilterDataItemType<T, TFilter> = {
    name: string,
    columns: DatalistTableColumnType<T, TFilter, any>[],
    value: DatalistDataSourceFilterDefinitionType
    onChange: (value: DatalistDataSourceFilterDefinitionType | undefined) => void
}

const DatalistFilterDataItem = <T, TFilter>(props: DatalistFilterDataItemType<T, TFilter>) => {
    const [t] = useTranslation();
    const fields = props.columns.filter(c => c.filter);
    const fieldDropdownConfig: DropdownInputPropsType<DatalistTableColumnType<T, TFilter, any>, TFilter> = {
        dataSource: {
            data: [...fields],
            search: 'off'
        },
        getItemId(i) {
            return i.name;
        },
        view: {
            inputView(i?) {
                if(i?.filter?.title) return getTranslationText(i.filter.title, t);
                return typeof i?.title === 'string' ? getTranslationText(i.title, t) : (t(`fields.${i?.name}.shortName`) || '');
            }
        },
        required: true,
        placeholder: t('data-list.filter.property')
    }

    const conditionDropdownConfig = (column: DatalistTableColumnType<T, TFilter, any> | undefined) => {
        const config: DropdownInputPropsType<Conditions> = {
            dataSource: {
                data:  column?.filter?.type ? filterConditions[column!.filter!.type!] : [],
                search: 'off'
            },
            getItemId(i) {
                return i;
            },
            view: {
                inputView(i?) {
                    return i ? t('data-list.filter.conditions.' + i) : '';
                }
            },
            required: true,
            placeholder: t('data-list.filter.condition')
        }
        return config;
    }

    const field = () => {
        const f = props.value.name ? fields.find(x => x.name === props.value.name) : undefined;
        return f;
    }

    const onchangeColumn = (column: DatalistTableColumnType<T, TFilter, any> | undefined) => {
        let condition = undefined;
        let value = undefined;
        if (column && props.value) {
            const pastColumn = fields.find(x => x.name === props.value.name)?.filter?.type?.replace('null', '');
            const thisColumn = column.filter?.type?.replace('null', '');
            if (pastColumn?.toLocaleLowerCase() === thisColumn?.toLocaleLowerCase()) {
                condition = props.value.condition;
                value = props.value.value;
            }
        }
        props.onChange({
            name: column?.name || '',
            condition,
            value,
            id: props.value.id
        })
    }

    const onChangeCondition = (condition: Conditions | undefined) => {
        props.onChange({
            name: props.value.name,
            condition: condition,
            value: undefined,
            id: props.value.id
        });
    }

    const onChangeValue = (value: T | undefined) => {
        props.onChange({
            name: props.value.name,
            condition: props.value.condition,
            value: value,
            id: props.value.id
        });
    }

    return (
        <div className="bg-neutral-100 dark:bg-neutral-900 my-2 lg:flex w-full">
            <div className="lg:flex">
                <DropdownInput placeholder={props.value?.name || ''} {...fieldDropdownConfig} value={field()} onChange={onchangeColumn} />
                <If condition={props.value.name}>
                    <DropdownInput {...conditionDropdownConfig(field())} value={props.value.condition} onChange={onChangeCondition} />
                </If>
                <If condition={props.value.condition && props.value.condition !== Conditions.null && props.value.condition !== Conditions.nnull}>
                    <DatalistFilterInput
                        name={props.name}
                        column={field()!}
                        value={props.value}
                        onChange={onChangeValue}
                    />
                </If>
            </div>
            <div className="w-full text-right lg:w-auto lg:my-auto">
                <Button type="button" theme="link" className="text-danger" title={t('button.remove')} onClick={() => props.onChange(undefined)}>
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </div>
        </div>
    );
}

export default DatalistFilterDataItem;
